import { Record } from 'immutable';
import { ApiDonorStdFile, DonorStdFile } from './DonorStdFile';

export type ApiDonorStd = {
  collected_on: string;
  hiv: string,
  syphilis: string;
  chlamydia: string;
  gonorrhea: string;
  trichomoniasis: string;
  candidiasis: string;
  hepatitis_b: string;
  hepatitis_c: string;
  status: string;
  expired: boolean;
  comments: string;
  donor_std_files: ApiDonorStdFile[];
};

const DonorStdBase = Record({
  collectedOn: "",
  hiv: "",
  syphilis: "",
  chlamydia: "",
  gonorrhea: "",
  trichomoniasis: "",
  candidiasis: "",
  hepatitisB: "",
  hepatitisC: "",
  status: "",
  expired: false,
  comments: "",
  donorStdFiles: [] as DonorStdFile[],
});

export class DonorStd extends DonorStdBase {
  constructor(apiDonorStd?: ApiDonorStd) {
    super({
      collectedOn: apiDonorStd?.collected_on,
      hiv: apiDonorStd?.hiv || 'MINUS',
      syphilis: apiDonorStd?.syphilis || 'MINUS',
      chlamydia: apiDonorStd?.chlamydia || 'MINUS',
      gonorrhea: apiDonorStd?.gonorrhea || 'MINUS',
      trichomoniasis: apiDonorStd?.trichomoniasis || 'NA',
      candidiasis: apiDonorStd?.candidiasis || 'NA',
      hepatitisB: apiDonorStd?.hepatitis_b || 'NA',
      hepatitisC: apiDonorStd?.hepatitis_c || 'NA',
      status: apiDonorStd?.status,
      expired: apiDonorStd?.expired,
      comments: apiDonorStd?.comments,
      donorStdFiles: (apiDonorStd?.donor_std_files || []).map((file) => new DonorStdFile(file)),
    })
  }

  get certified(): boolean {
    if (this.status !== 'CERTIFIED') { return false }

    return this.inOneYear;
  }

  get inOneYear(): boolean {
    if (this.collectedOn === '') { return false }

    const collectedDate = new Date(this.collectedOn + 'T00:00:00');
    const today = new Date();
    const lastYear = new Date(today.getFullYear()-1, today.getMonth(), today.getDate());
    return collectedDate > lastYear;
  }
  
  get in6Month(): boolean {
    if (this.collectedOn === '') { return false }

    const collectedDate = new Date(this.collectedOn + 'T00:00:00');
    const today = new Date();
    const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
    return collectedDate > sixMonthsAgo;
  }

  get total(): string {
    const targets = [
      this.hiv,
      this.syphilis,
      this.chlamydia,
      this.gonorrhea,
      this.trichomoniasis,
      this.candidiasis,
      this.hepatitisB,
      this.hepatitisC,
    ];
    return targets.includes('PLUS') ? 'PLUS' : 'MINUS';
  }

  get totalText(): string {
    switch (this.total) {
      case 'MINUS':
        return '陽性なし';
      case 'PLUS':
        return '陽性あり';
      default:
        return '---';
    }
  }
  readonly totalAlert: boolean = this.total === 'PLUS';

  readonly fileText: string = this.donorStdFiles.length > 0 ? '添付済み' : '未添付';
  readonly fileAlert: boolean = this.donorStdFiles.length === 0;

  get statusText(): string {
    switch (this.status) {
      case 'DRAFT':
        return '未提出';
      case 'SUBMITTED':
        return '確認中';
      case 'REMANDED':
        return '要確認';
      case 'CERTIFIED':
        if (!this.inOneYear) {
          return '期限切れ';
        } else {
          return '登録済';
        }
      default:
        return '未提出';
    }
  }
  readonly statusAlert: boolean = !this.certified && this.status !== 'SUBMITTED';

  readonly collectedOnText: string = this.collectedOn && this.collectedOn.replace(/-/g, '/');

  isValidForRegistration(): boolean {
    const requiredFilled = !!this.collectedOn
        && !!this.hiv
        && !!this.syphilis
        && !!this.chlamydia
        && !!this.gonorrhea
        && !!this.donorStdFiles
        && this.donorStdFiles.length > 0;

    return this.status === 'DRAFT' && this.total === 'MINUS' && requiredFilled && this.in6Month;
  }
}
